import type { Scaled, ScaledPosition } from 'react-pdf-highlighter';
import type { BoundingRect, PdfPointViewport, SourceZone } from '../types';

const round = (value: number) => Math.round(value * 100) / 100;

const isInside = (inner: BoundingRect, outer: BoundingRect) =>
  inner.x1 >= outer.x1 &&
  inner.y1 >= outer.y1 &&
  inner.x2 <= outer.x2 &&
  inner.y2 <= outer.y2;

/**
 * A react-pdf-highlighter text selection → a `SourceZone` in the BE's format:
 * PDF points, y from the page bottom, one rect per line.
 * pdf.js does the conversion (the inverse of how the viewer draws
 * `usePdfCoordinates` highlights), so rotated and cropped pages round-trip.
 */
export const toSourceZone = (
  position: ScaledPosition,
  quote: string,
  viewport: PdfPointViewport,
): SourceZone => {
  const [xMin, yMin, xMax, yMax] = viewport.viewBox;
  const pageWidth = round(xMax - xMin);
  const pageHeight = round(yMax - yMin);
  const pageNumber = position.pageNumber;

  const toPdfRect = (rect: Scaled): BoundingRect => {
    // `rect` is in viewport pixels, measured on a viewport of
    // `rect.width` × `rect.height`.
    const scaleX = viewport.width / rect.width;
    const scaleY = viewport.height / rect.height;
    const [ax, ay] = viewport.convertToPdfPoint(
      rect.x1 * scaleX,
      rect.y1 * scaleY,
    );
    const [bx, by] = viewport.convertToPdfPoint(
      rect.x2 * scaleX,
      rect.y2 * scaleY,
    );
    return {
      x1: round(Math.min(ax, bx)),
      y1: round(Math.min(ay, by)),
      x2: round(Math.max(ax, bx)),
      y2: round(Math.max(ay, by)),
      width: pageWidth,
      height: pageHeight,
      page_number: pageNumber,
    };
  };

  // react-pdf-highlighter can report a line twice, one box inside the other.
  const allRects = position.rects.map(toPdfRect);
  const rects = allRects.filter(
    (rect, index) =>
      !allRects.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          isInside(rect, other) &&
          // Of two identical rects, keep the first.
          (!isInside(other, rect) || otherIndex < index),
      ),
  );

  return {
    quote,
    position: {
      use_pdf_coordinates: true,
      bounding_rect: toPdfRect(position.boundingRect),
      rects,
      page_number: pageNumber,
    },
    spans_multiple_lines: rects.length > 1,
  };
};
